import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { injectIntl, useIntl } from "@ai360/core/intl";
import { toCamelCase, preventBubbleUp, formatDate } from "~/admin/utils";
import { SteeringDataAPI } from "@ai360/core";

import { Button, DataTable, LoaderTypes, Loader, DialogBox, DialogBoxFooterType } from "~/core";
import { ISearchData, ISortOptions } from "~/core/components/tables/interfaces";
import ApiResultsWrapper from "~/core/api/components/api-results-wrapper";

import HierarchyFilter from "../component/hierarchy-filter";
import SlidingPanel from "~/sliding-panel/sliding-panel";

import { steeringDataActions, steeringDataSelectors } from "./data";
import { service, defaultSort } from "./service";
import * as model from "./model";
import { messages } from "./i18n-messages";
import { SteeringDataForm } from "./steering-data-form";
import { getEmbeddingStatusLabel } from "./status";
import "./steering-data-page.css";

type ISteeringDataRecord = SteeringDataAPI.ISteeringDataRecord;

interface SteeringDataGridRow {
    steeringDataGuid: string;
    name: string;
    location: string;
    fileName: string;
    lastModified: string;
    embeddingStatus: string;
    sourceType: "file" | "url-text" | "url-files";
    canDelete: boolean;
}

export const SteeringDataPage_ = () => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const records = useAppSelector(steeringDataSelectors.getSteeringDataRecords);
    const loading = useAppSelector(steeringDataSelectors.getSteeringDataLoading);

    const [filter, setFilter] = useState<Record<string, string>>({});
    const [sortOptions, setSortOptions] = useState<ISortOptions>(defaultSort as ISortOptions);
    const [autoSearchData, setAutoSearchData] = useState<ISearchData>();
    const [itemToDelete, setItemToDelete] = useState<ISteeringDataRecord | null>(null);
    const [showAddEditPanel, setShowAddEditPanel] = useState(false);
    const [selectedRow, setSelectedRow] = useState<ISteeringDataRecord | null>(null);
    const [skip, setSkip] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(50);

    // Fetch on mount; the active flag gates real-time refreshes to this screen.
    useEffect(() => {
        dispatch(steeringDataActions.setSteeringDataScreenActive(true));
        dispatch(steeringDataActions.fetchSteeringDataList());
        return () => {
            dispatch(steeringDataActions.setSteeringDataScreenActive(false));
        };
    }, [dispatch]);

    // Transform records for grid display (full filtered + sorted set)
    const allGridData: SteeringDataGridRow[] = useMemo(() => {
        let data = (records || []).map((record: ISteeringDataRecord) => ({
            steeringDataGuid: record.steeringDataGuid,
            name: record.name,
            location: (record.hierarchyLevels || [])
                .map((level) => level.orgLevelName)
                .sort()
                .join(", "),
            fileName: record.sourceType === "file" ? record.fileName || "" : record.publicUrl || "",
            lastModified: formatDate(record.lastModified, "MM/DD/YYYY h:mm:ss A") || "",
            embeddingStatus: getEmbeddingStatusLabel(record.embeddingStatus),
            sourceType: record.sourceType,
            canDelete: true,
        }));

        // Apply column-based search filtering (case-insensitive substring matching)
        if (filter[model.PROPS_NAME]) {
            data = data.filter((row) =>
                row.name.toLowerCase().includes(filter[model.PROPS_NAME].toLowerCase())
            );
        }
        if (filter[model.PROPS_LOCATION]) {
            data = data.filter((row) =>
                row.location.toLowerCase().includes(filter[model.PROPS_LOCATION].toLowerCase())
            );
        }
        if (filter[model.PROPS_FILE_NAME]) {
            data = data.filter((row) =>
                row.fileName.toLowerCase().includes(filter[model.PROPS_FILE_NAME].toLowerCase())
            );
        }
        if (filter[model.PROPS_LAST_MODIFIED]) {
            data = data.filter((row) =>
                row.lastModified
                    .toLowerCase()
                    .includes(filter[model.PROPS_LAST_MODIFIED].toLowerCase())
            );
        }

        // Apply sorting
        const sortAttribute = toCamelCase(
            (sortOptions as any)?.FieldName || model.PROPS_NAME
        ) as keyof SteeringDataGridRow;
        const sortAsc = ((sortOptions as any)?.Sort?.Direction || "ASC") === "ASC";

        data = data.sort((a, b) => {
            const aVal = (a[sortAttribute] || "") as string;
            const bVal = (b[sortAttribute] || "") as string;
            if (aVal < bVal) return sortAsc ? -1 : 1;
            if (aVal > bVal) return sortAsc ? 1 : -1;
            return 0;
        });

        return data;
    }, [records, filter, sortOptions]);

    // Client-side pagination: show only the current page's slice.
    const gridData: SteeringDataGridRow[] = useMemo(
        () => allGridData.slice(skip, skip + pageSize),
        [allGridData, skip, pageSize]
    );

    // Column search handler (reset to first page when the filter changes)
    const onFilterChange = (attr: string, value: string) => {
        const attrName = toCamelCase(attr);
        setSkip(0);
        setFilter((prev) => ({ ...prev, [attrName]: value || undefined }));
    };

    // Auto-search list for column search (searches the full dataset, not just the page)
    const getAutoSearchList = (attr: string, value: string) => {
        const attrName = toCamelCase(attr);
        const autoSearch: ISearchData = {
            searchName: attrName,
            response: [],
        };

        const uniqueValues = Array.from(
            new Set(allGridData.map((row) => row[attrName as keyof SteeringDataGridRow] as string))
        )
            .filter(Boolean)
            .filter((val) => val.toLowerCase().includes(value.toLowerCase()))
            .map((val) => ({ value: val }));

        autoSearch.response = uniqueValues;
        setAutoSearchData(autoSearch);
        return autoSearch;
    };

    // Sort handler (reset to first page when the sort changes)
    const onSortOption = ({ sortOptions: nextSortOptions }) => {
        setSkip(0);
        setSortOptions(nextSortOptions || (defaultSort as ISortOptions));
    };

    // Pagination handler (client-side): the paginator reports skip + pageSize.
    const onPaginatorChange = ({ skip: nextSkip, pageSize: nextPageSize }) => {
        setSkip(nextSkip || 0);
        setPageSize(Number(nextPageSize) || 50);
    };

    // Add/Edit panel handler
    const onShowAddEditPanel = (_: any, action: "ADD" | "EDIT", editData?: any) => {
        console.log("[SteeringData] onShowAddEditPanel", { action, editData });
        if (action === "EDIT" && editData) {
            const guid = editData.steeringDataGuid || editData;
            const record = records.find((r) => r.steeringDataGuid === guid);
            console.log("[SteeringData] Found record for edit:", record);
            setSelectedRow(record || null);
        } else {
            setSelectedRow(null);
        }
        setShowAddEditPanel(true);
    };

    // Close panel
    const closePanel = () => {
        setSelectedRow(null);
        setShowAddEditPanel(false);
    };

    // Delete handler
    const onDeleteAction = (deleteData: { selectedItems?: any[] }) => {
        console.log("[SteeringData] onDeleteAction", deleteData);
        if (!deleteData.selectedItems?.length) return;
        const guid = deleteData.selectedItems[0];
        const record = records.find((r) => r.steeringDataGuid === guid);
        console.log("[SteeringData] Setting itemToDelete:", record);
        if (record) {
            setItemToDelete(record);
        }
    };

    const onConfirmDelete = () => {
        console.log("[SteeringData] onConfirmDelete", itemToDelete?.steeringDataGuid);
        if (!itemToDelete) return;
        dispatch(steeringDataActions.deleteSteeringData(itemToDelete.steeringDataGuid));
        setItemToDelete(null);
    };

    return (
        <ApiResultsWrapper clearErrorOnUnmount={false}>
            <div className="content-table-container steering-data-page">
                {!loading ? null : <Loader type={LoaderTypes.LINE_SCALE} />}

                <div className="filter-header-container">
                    <div className="filter-container">
                        <HierarchyFilter
                            onFilterChange={(location) =>
                                setFilter((prev) => ({
                                    ...prev,
                                    [model.PROPS_LOCATION]: location
                                        ? location["orgLevelName"]
                                        : undefined,
                                }))
                            }
                        />
                    </div>
                </div>

                <DataTable
                    formatMessage={formatMessage as any}
                    autoSearchList={autoSearchData}
                    isEditable
                    isCheckbox={false}
                    messages={messages as any}
                    getAutoSearchList={getAutoSearchList}
                    onFilterChange={onFilterChange}
                    onSortOption={onSortOption}
                    onPaginatorChange={onPaginatorChange}
                    openAddEditPanel={onShowAddEditPanel}
                    records={gridData}
                    service={service}
                    totalCount={allGridData.length}
                    deleteSelected={onDeleteAction}
                    setLastEditedRow={() => {
                        /* intentionally empty */
                    }}
                />

                {!showAddEditPanel ? null : (
                    <form onSubmit={(event) => preventBubbleUp(event)}>
                        <SlidingPanel
                            close={closePanel}
                            component={SteeringDataForm}
                            navigateTo={{
                                parentNameCode: "101",
                                childNameCode: "436",
                            }}
                            closePanel={closePanel}
                            selectedRecord={selectedRow}
                        >
                            <Button
                                type="save"
                                forceSubmit
                                onClick={() => {
                                    document.dispatchEvent(
                                        new CustomEvent("steering-data-form-submit")
                                    );
                                }}
                            />
                            <Button type="cancel" onClick={closePanel} />
                        </SlidingPanel>
                    </form>
                )}
            </div>
            <DialogBox
                action="Delete"
                draggable={true}
                className="delete-confirmation-modal"
                footerType={DialogBoxFooterType.ACTION_CANCEL}
                isOpen={itemToDelete !== null}
                onAction={onConfirmDelete}
                onClose={() => setItemToDelete(null)}
                title={formatMessage(messages.deleteConfirmationTitle)}
            >
                <p>
                    {itemToDelete
                        ? formatMessage(messages.deleteConfirmationMessage, {
                              name: itemToDelete.name,
                              type: itemToDelete.sourceType === "file" ? "File" : "URL",
                          })
                        : ""}
                </p>
            </DialogBox>
        </ApiResultsWrapper>
    );
};

export const SteeringDataPage = injectIntl(SteeringDataPage_);
